import React, { useState, useCallback, useRef, useEffect } from 'react';
import {
  ChevronDown,
  Upload,
  Download,
  ExternalLink,
  X,
  Sparkles,
  ChevronLeft,
  Image as ImageIcon,
} from 'lucide-react';
import {
  Button,
  Textarea,
  Badge,
  Alert,
  Spinner,
  Card,
  CardContent,
} from '@/components/ui';
import { useNotificationStore } from '@/stores/notificationStore';
import {
  templateHubService,
  type TemplateListItem,
  type TemplateInputs,
  type GenerationResult,
} from '@/services/templateHubService';
import { cn } from '@/lib/utils';

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

/** Hook: true when viewport matches a min-width media query (SSR-safe). */
function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() =>
    typeof window !== 'undefined' ? window.matchMedia(query).matches : false
  );

  useEffect(() => {
    const mql = window.matchMedia(query);
    const handler = (e: MediaQueryListEvent) => setMatches(e.matches);
    mql.addEventListener('change', handler);
    setMatches(mql.matches);
    return () => mql.removeEventListener('change', handler);
  }, [query]);

  return matches;
}

/** Expandable section for displaying source inputs. Collapsed on mobile, open on desktop. */
function CollapsibleSection({
  title,
  count,
  children,
}: {
  title: string;
  count: number;
  children: React.ReactNode;
}) {
  const isDesktop = useMediaQuery('(min-width: 768px)');
  const [open, setOpen] = useState(isDesktop);

  // Sync default when breakpoint changes
  useEffect(() => {
    setOpen(isDesktop);
  }, [isDesktop]);

  if (count === 0) return null;

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-3 py-2 text-xs font-medium text-gray-600 hover:bg-gray-50 transition-colors"
        aria-expanded={open}
      >
        <span>
          {title} <span className="text-gray-400 font-normal">({count})</span>
        </span>
        <ChevronDown
          className={cn('w-4 h-4 transition-transform duration-200', open && 'rotate-180')}
        />
      </button>
      {open && <div className="border-t border-gray-200 px-3 py-2.5 space-y-2 bg-gray-50/50">{children}</div>}
    </div>
  );
}

/** Displays a single prompt text from source inputs. */
function PromptPreview({ text }: { text: string }) {
  return (
    <div className="text-sm text-gray-600 bg-white rounded-md border border-gray-100 px-3 py-2 line-clamp-3">
      {text}
    </div>
  );
}

/** Displays a single image thumbnail from source inputs. */
function ImagePreview({ url, name }: { url: string; name: string | null }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <>
      <button
        type="button"
        onClick={() => setExpanded(true)}
        className="group relative flex flex-col items-center gap-1 focus:outline-none"
      >
        <img
          src={url}
          alt={name || 'Reference image'}
          className="w-20 h-20 sm:w-24 sm:h-24 object-cover rounded-lg border border-gray-200 shadow-sm group-hover:ring-2 group-hover:ring-primary-300 transition-all"
          loading="lazy"
        />
        {name && (
          <span className="text-[11px] text-gray-400 truncate max-w-[80px] sm:max-w-[96px]">
            {name}
          </span>
        )}
      </button>

      {/* Lightbox overlay */}
      {expanded && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
          onClick={() => setExpanded(false)}
        >
          <div className="relative max-w-lg w-full" onClick={(e) => e.stopPropagation()}>
            <img
              src={url}
              alt={name || 'Reference image'}
              className="w-full h-auto rounded-xl shadow-2xl"
            />
            <button
              type="button"
              onClick={() => setExpanded(false)}
              className="absolute -top-3 -right-3 w-8 h-8 bg-white rounded-full shadow-lg flex items-center justify-center text-gray-600 hover:text-gray-900"
              aria-label="Close preview"
            >
              <X className="w-4 h-4" />
            </button>
            {name && (
              <p className="text-center text-sm text-white/80 mt-2">{name}</p>
            )}
          </div>
        </div>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// File upload area
// ---------------------------------------------------------------------------

function ImageUploadArea({
  files,
  onAdd,
  onRemove,
}: {
  files: File[];
  onAdd: (newFiles: File[]) => void;
  onRemove: (index: number) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      const dropped = Array.from(e.dataTransfer.files).filter((f) =>
        f.type.startsWith('image/')
      );
      if (dropped.length) onAdd(dropped);
    },
    [onAdd]
  );

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const selected = Array.from(e.target.files || []);
      if (selected.length) onAdd(selected);
      // Reset input so same file can be re-selected
      e.target.value = '';
    },
    [onAdd]
  );

  return (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-gray-700">Upload Images</label>

      {/* Thumbnails of already-selected files */}
      {files.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {files.map((file, i) => (
            <div key={`${file.name}-${i}`} className="relative group">
              <img
                src={URL.createObjectURL(file)}
                alt={file.name}
                className="w-16 h-16 object-cover rounded-md border border-gray-200"
              />
              <button
                type="button"
                onClick={() => onRemove(i)}
                className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-gray-700 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                aria-label={`Remove ${file.name}`}
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Drop zone */}
      <div
        onDragOver={(e) => e.preventDefault()}
        onDrop={handleDrop}
        onClick={() => inputRef.current?.click()}
        className="flex flex-col items-center justify-center py-4 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-primary-400 hover:bg-primary-50/30 transition-colors"
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') inputRef.current?.click();
        }}
      >
        <Upload className="w-5 h-5" />
        <span className="text-sm text-gray-500 mt-1">
          Drop images here or <span className="text-primary-600 font-medium">browse</span>
        </span>
        <span className="text-xs text-gray-400 mt-0.5">PNG, JPG, WebP — up to 25 MB each</span>
      </div>

      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        multiple
        onChange={handleChange}
        className="sr-only"
        aria-label="Upload images"
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Generation output display
// ---------------------------------------------------------------------------

function GenerationOutput({ result }: { result: GenerationResult }) {
  const handleDownload = useCallback(() => {
    const url = result.imageUrl || result.videoUrl;
    if (!url) return;
    const a = document.createElement('a');
    a.href = url;
    a.download = `template-generation.${result.mediaType === 'video' ? 'mp4' : 'png'}`;
    a.click();
  }, [result]);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-medium text-gray-700">Output</h4>
        {(result.imageUrl || result.videoUrl) && (
          <Button variant="ghost" size="sm" onClick={handleDownload} leftIcon={<Download className="w-4 h-4" />}>
            Download
          </Button>
        )}
      </div>

      {result.mediaType === 'image' && result.imageUrl && (
        <img
          src={result.imageUrl}
          alt="Generated image"
          className="w-full h-auto rounded-lg border border-gray-200 shadow-sm"
        />
      )}

      {result.mediaType === 'video' && result.videoUrl && (
        <video
          src={result.videoUrl}
          controls
          className="w-full rounded-lg border border-gray-200 shadow-sm"
          poster={result.thumbnailUrl || undefined}
        />
      )}

      {result.mediaType === 'text' && result.textContent && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-sm text-gray-700 whitespace-pre-wrap max-h-64 overflow-y-auto">
          {result.textContent}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

interface TemplateDetailPanelProps {
  template: TemplateListItem;
  onTemplateUpdated: (updated: TemplateListItem) => void;
  onBack?: () => void;
}

export function TemplateDetailPanel({ template, onTemplateUpdated, onBack }: TemplateDetailPanelProps) {
  const notify = useNotificationStore();

  // Inputs state
  const [inputs, setInputs] = useState<TemplateInputs | null>(null);
  const [inputsLoading, setInputsLoading] = useState(false);
  const [inputsError, setInputsError] = useState<string | null>(null);

  // Editing state
  const [isEditingName, setIsEditingName] = useState(false);
  const [editName, setEditName] = useState(template.name);
  const [isEditingDesc, setIsEditingDesc] = useState(false);
  const [editDesc, setEditDesc] = useState(template.description || '');

  // Generation state
  const [additionalText, setAdditionalText] = useState('');
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationResult, setGenerationResult] = useState<GenerationResult | null>(null);
  const [generationError, setGenerationError] = useState<string | null>(null);

  // Load inputs when template changes
  useEffect(() => {
    let cancelled = false;
    setInputs(null);
    setInputsError(null);
    setInputsLoading(true);
    setGenerationResult(null);
    setGenerationError(null);
    setAdditionalText('');
    setUploadedFiles([]);
    setEditName(template.name);
    setEditDesc(template.description || '');
    setIsEditingName(false);
    setIsEditingDesc(false);

    templateHubService
      .getInputs(template.id)
      .then((data) => {
        if (!cancelled) setInputs(data);
      })
      .catch((err) => {
        if (!cancelled) setInputsError(err.message || 'Failed to load template inputs');
      })
      .finally(() => {
        if (!cancelled) setInputsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [template.id]);

  // Save name
  const saveName = useCallback(async () => {
    const trimmed = editName.trim();
    if (!trimmed || trimmed === template.name) {
      setEditName(template.name);
      setIsEditingName(false);
      return;
    }
    try {
      const updated = await templateHubService.update(template.id, { name: trimmed });
      onTemplateUpdated(updated);
      setIsEditingName(false);
      notify.success('Template name updated');
    } catch (err: any) {
      notify.error(err.message || 'Failed to update name');
    }
  }, [editName, template, onTemplateUpdated, notify]);

  // Save description
  const saveDesc = useCallback(async () => {
    const trimmed = editDesc.trim();
    if (trimmed === (template.description || '')) {
      setIsEditingDesc(false);
      return;
    }
    try {
      const updated = await templateHubService.update(template.id, { description: trimmed || undefined });
      onTemplateUpdated(updated);
      setIsEditingDesc(false);
      notify.success('Template description updated');
    } catch (err: any) {
      notify.error(err.message || 'Failed to update description');
    }
  }, [editDesc, template, onTemplateUpdated, notify]);

  // Generate
  const handleGenerate = useCallback(async () => {
    setIsGenerating(true);
    setGenerationError(null);
    setGenerationResult(null);

    try {
      const result = await templateHubService.generate(template.id, {
        additionalText: additionalText.trim() || undefined,
        images: uploadedFiles.length > 0 ? uploadedFiles : undefined,
      });
      setGenerationResult(result);
      notify.success('Generation complete');
    } catch (err: any) {
      const message = err.message || 'Generation failed';
      setGenerationError(message);
      notify.error(message);
    } finally {
      setIsGenerating(false);
    }
  }, [template.id, additionalText, uploadedFiles, notify]);

  // File handlers
  const handleAddFiles = useCallback((newFiles: File[]) => {
    setUploadedFiles((prev) => [...prev, ...newFiles]);
  }, []);

  const handleRemoveFile = useCallback((index: number) => {
    setUploadedFiles((prev) => prev.filter((_, i) => i !== index));
  }, []);

  // Preset label helper
  const presetLabel = inputs?.preset
    ? `${inputs.preset.model} · ${inputs.preset.mediaType} · ${inputs.preset.aspectRatio || ''} ${inputs.preset.size || ''}`
    : null;

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Header */}
      <div className="px-4 sm:px-6 py-4 sm:py-5 border-b border-gray-200 shrink-0">
        {/* Mobile back button */}
        {onBack && (
          <button
            type="button"
            onClick={onBack}
            className="md:hidden flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800 mb-3 -ml-1 transition-colors"
          >
            <ChevronLeft className="w-4 h-4" />
            Back to list
          </button>
        )}
        {/* Name */}
        {isEditingName ? (
          <input
            type="text"
            value={editName}
            onChange={(e) => setEditName(e.target.value)}
            onBlur={saveName}
            onKeyDown={(e) => {
              if (e.key === 'Enter') saveName();
              if (e.key === 'Escape') {
                setEditName(template.name);
                setIsEditingName(false);
              }
            }}
            className="text-lg sm:text-xl font-bold text-gray-900 w-full border-b-2 border-primary-400 outline-none bg-transparent pb-0.5"
            autoFocus
          />
        ) : (
          <h2
            className="text-lg sm:text-xl font-bold text-gray-900 cursor-pointer hover:text-primary-700 transition-colors"
            onClick={() => setIsEditingName(true)}
            title="Click to edit name"
          >
            {template.name}
          </h2>
        )}

        {/* Description */}
        {isEditingDesc ? (
          <textarea
            value={editDesc}
            onChange={(e) => setEditDesc(e.target.value)}
            onBlur={saveDesc}
            onKeyDown={(e) => {
              if (e.key === 'Escape') {
                setEditDesc(template.description || '');
                setIsEditingDesc(false);
              }
            }}
            rows={2}
            className="mt-2 w-full text-sm text-gray-600 border border-primary-300 rounded-md px-2 py-1.5 outline-none focus:ring-2 focus:ring-primary-400 resize-none bg-transparent"
            placeholder="Add a description..."
            autoFocus
          />
        ) : (
          <p
            className={cn(
              'mt-1 text-sm cursor-pointer transition-colors',
              template.description ? 'text-gray-600 hover:text-primary-600' : 'text-gray-400 italic hover:text-primary-500'
            )}
            onClick={() => setIsEditingDesc(true)}
            title="Click to edit description"
          >
            {template.description || 'Click to add a description...'}
          </p>
        )}

        {/* Source canvas link */}
        {template.source_canvas_name && (
          <a
            href={`/canvas/${template.source_canvas_id}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 mt-2 text-xs text-gray-400 hover:text-primary-600 transition-colors"
          >
            <ExternalLink className="w-3.5 h-3.5" />
            Source: {template.source_canvas_name}
          </a>
        )}
      </div>

      {/* Scrollable body */}
      <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4 sm:py-5 space-y-5">
        {/* Loading state */}
        {inputsLoading && (
          <div className="flex items-center justify-center py-12">
            <Spinner size="md" />
          </div>
        )}

        {/* Error state */}
        {inputsError && (
          <Alert variant="error" title="Failed to load template">
            {inputsError}
          </Alert>
        )}

        {/* Source inputs section */}
        {inputs && !inputsLoading && (
          <>
            <div className="space-y-2">
              <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
                Source Inputs
              </h3>

              <CollapsibleSection
                title="Prompts"
                count={inputs.prompts.length}
              >
                {inputs.prompts.map((p) => (
                  <PromptPreview key={p.id} text={p.text} />
                ))}
              </CollapsibleSection>

              <CollapsibleSection
                title="Images"
                count={inputs.images.length}
              >
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                  {inputs.images.map((img) => (
                    <ImagePreview key={img.id} url={img.imageUrl} name={img.originalName} />
                  ))}
                </div>
              </CollapsibleSection>

              {presetLabel && (
                <div className="flex items-center gap-2 text-xs text-gray-500">
                  <Badge variant="secondary" className="text-xs">
                    {inputs.preset.mediaType}
                  </Badge>
                  <span className="truncate">{presetLabel}</span>
                </div>
              )}
            </div>

            {/* Divider */}
            <hr className="border-gray-200" />

            {/* Generate section */}
            <div className="space-y-4">
              <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
                Generate
              </h3>

              <Textarea
                label="Additional Text"
                placeholder="Add extra instructions, context, or prompt text..."
                value={additionalText}
                onChange={(e) => setAdditionalText(e.target.value)}
                rows={3}
                className="text-sm placeholder:text-sm"
                hint="This text is appended to the template's existing prompts"
              />

              <ImageUploadArea
                files={uploadedFiles}
                onAdd={handleAddFiles}
                onRemove={handleRemoveFile}
              />

              {generationError && (
                <Alert variant="error" title="Generation failed">
                  {generationError}
                </Alert>
              )}

              <Button
                variant="primary"
                fullWidth
                onClick={handleGenerate}
                isLoading={isGenerating}
                disabled={isGenerating}
                leftIcon={<Sparkles className="w-4 h-4" />}
                size="lg"
              >
                {isGenerating ? 'Generating...' : 'Generate'}
              </Button>
            </div>

            {/* Output section */}
            {generationResult && (
              <>
                <hr className="border-gray-200" />
                <GenerationOutput result={generationResult} />
              </>
            )}
          </>
        )}

        {/* Source node missing */}
        {inputs === null && !inputsLoading && !inputsError && (
          <div className="text-center py-8 text-gray-400">
            <p>Select a template to see its details</p>
          </div>
        )}
      </div>
    </div>
  );
}
